/** @format */

import React, { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import {
  TextField,
  Button,
  InputAdornment,
  IconButton,  
} from "@mui/material";
import Visibility from "@mui/icons-material/Visibility";
import VisibilityOff from "@mui/icons-material/VisibilityOff";
import "./index.scss";
import { invokeApi, HTTP_METHODS } from "../../utils/http-service";
import { INTRANETHOSTNAME } from "../../packages/common-utilities/utils/end-points";
import { REST_URLS } from "../../packages/common-utilities/utils/end-points/intranet";
import { setOfflineData, getOfflineData } from "../../utils/offline-services";
import { toast } from "react-toastify";
import loginLogo from "../../resources/images/white-logo.png";
import { Stack } from "rsuite";
import { MS_SSO_MESSAGE_SOURCE } from "../microsoft-sso-callback";

const MicrosoftLogo = () => (
  <svg width="18" height="18" viewBox="0 0 21 21">
    <rect x="1" y="1" width="9" height="9" fill="#f25022" />
    <rect x="11" y="1" width="9" height="9" fill="#7fba00" />
    <rect x="1" y="11" width="9" height="9" fill="#00a4ef" />
    <rect x="11" y="11" width="9" height="9" fill="#ffb900" />
  </svg>
);

const Login = () => {
  const navigate = useNavigate();
  const [loginDetails, setLoginDetails] = useState({
    email: "",
    password: "",
  });
  const [showPassword, setShowPassword] = useState(false);

  useEffect(() => {
    if (getOfflineData("user")) {
      navigate("/");
    }
  }, []);

  const onInputChange = (event) => {
    setLoginDetails({
      ...loginDetails,
      [event.target.name]: event.target.value,
    });
  };

  const login = () => {
    let payload = {
      password: loginDetails.password,
    };
    if (loginDetails.email.includes("@")) {
      payload["email"] = loginDetails.email;
    } else {
      payload["userId"] = loginDetails.email;
    }
    invokeApi(
      HTTP_METHODS.POST,
      `${INTRANETHOSTNAME}${REST_URLS.LOGIN}`,
      payload
    ).then((response) => {
      if (response.message) {
        toast.error(response.message);
        setLoginDetails({
          ...loginDetails,
          errorMsg: response.message,
        });
      } else {
        response.user && setOfflineData("user", response.user);
        response.tokens && setOfflineData("tokens", response.tokens);
        navigate("/");
      }
    });
  };

  const loginWithMicrosoft = () => {
    invokeApi(
      HTTP_METHODS.GET,
      `${INTRANETHOSTNAME}${REST_URLS.MS_SSO_CONFIG}`
    ).then((config) => {
      if (config.message) {
        toast.error(config.message);
        return;
      }
      const { tenantId, clientId, redirectUri, scope } = config;
      if (!tenantId || !clientId || !redirectUri) {
        toast.error("Sign in with Microsoft is not configured.");
        return;
      }

      const state = Math.random().toString(36).slice(2);
      const authorizeUrl = `https://login.microsoftonline.com/${tenantId}/oauth2/v2.0/authorize?${new URLSearchParams(
        {
          client_id: clientId,
          response_type: "code",
          response_mode: "query",
          redirect_uri: redirectUri,
          scope: scope,
          state: state,
        }
      ).toString()}`;

      const popup = window.open(
        authorizeUrl,
        "ms-sso-login",
        "width=500,height=650"
      );
      if (!popup) {
        toast.error("Please allow popups to sign in with Microsoft.");
        return;
      }

      const handleMessage = (event) => {
        if (
          event.origin !== window.location.origin ||
          event.data?.source !== MS_SSO_MESSAGE_SOURCE
        ) {
          return;
        }
        window.removeEventListener("message", handleMessage);

        if (event.data.state !== state) {
          toast.error("Sign in with Microsoft failed. Please try again.");
          return;
        }
        if (event.data.error || !event.data.code) {
          toast.error(
            event.data.errorDescription ||
              "Sign in with Microsoft was cancelled."
          );
          return;
        }

        invokeApi(
          HTTP_METHODS.POST,
          `${INTRANETHOSTNAME}${REST_URLS.MS_SSO_CALLBACK}`,
          { code: event.data.code, state: event.data.state }
        ).then((response) => {
          if (response.message) {
            toast.error(response.message);
          } else {
            response.user &&
              setOfflineData("user", {
                ...response.user,
                authProvider: "microsoft",
              });
            response.tokens && setOfflineData("tokens", response.tokens);
            navigate("/");
          }
        });
      };

      window.addEventListener("message", handleMessage);
    });
  };

  return (
    <div className="login-background">
      <div className="login-con">
        <h1>Welcome to Pulse</h1>
        <TextField
          size="small"
          label="Email / User Id"
          name="email"
          value={loginDetails.email}
          onChange={onInputChange}
          sx={{ mb: 4 }}
        />
        <TextField
          size="small"
          label="Password"
          type={showPassword ? "text" : "password"}
          name="password"
          value={loginDetails.password}
          onChange={onInputChange}
          sx={{ mb: 4 }}
          InputProps={{
            endAdornment: (
              <InputAdornment position="end">
                <IconButton onClick={() => setShowPassword(!showPassword)}>
                  {showPassword ? <VisibilityOff /> : <Visibility />}
                </IconButton>
              </InputAdornment>
            ),
          }}
        />
        <Stack justifyContent="flex-end" direction="row">
          <Button            
            variant="text"
            onClick={() => navigate(`/forgot-password`)}
          >
            Forgot password?
          </Button>
        </Stack>

        <Button
          variant="contained"
          color="primary"
          onClick={login}
          fullWidth={true}
          disabled={!loginDetails.email || !loginDetails.password}
          sx={{
            borderRadius: "20px",
            mt: 2,
            // width: "12rem",
            fontWeight: "bold",
          }}
        >
          Login
        </Button>

        <div className="sso-divider">
          <span>or</span>
        </div>
        <Button
          variant="outlined"
          onClick={loginWithMicrosoft}
          fullWidth={true}
          startIcon={<MicrosoftLogo />}
          sx={{
            borderRadius: "20px",
            fontWeight: "bold",
            textTransform: "none",
          }}
        >
          Sign in with Microsoft
        </Button>

        {loginDetails.errorMsg && (
          <span className="error-msg">{loginDetails.errorMsg}</span>
        )}
      </div>
      <p className="powered-by">
        Powered by <strong>Bellcorp Studio</strong>
      </p>
      <div className="white-logo si-hide-mobile">
        <img src={loginLogo} />
      </div>
    </div>
  );
};

export default Login;
